"use client";
import Link from "next/link";
import React, { useState, useEffect, useCallback } from "react";

interface TocItem {
  id: string;
  text: string;
  level: string;
}

interface TocGroup {
  h2: TocItem;
  children: TocItem[];
}

/** Group a flat list of h2/h3 TocItems into parent→children pairs. */
function groupTocItems(items: TocItem[]): TocGroup[] {
  const groups: TocGroup[] = [];

  for (const item of items) {
    if (item.level === "h2") {
      groups.push({ h2: item, children: [] });
    } else if (item.level === "h3") {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup) {
        lastGroup.children.push(item);
      }
    }
    // ⚠️ h3s that appear before any h2 are silently skipped — ensure content is well-formed
  }

  return groups;
}

export default function TableOfContents({ items }: { items: TocItem[] }) {
  const [activeId, setActiveId] = useState<string>("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [scrollProgress, setScrollProgress] = useState<Record<string, number>>(
    items.reduce((acc, item) => ({ ...acc, [item.id]: 0 }), {}),
  );

  const groups = groupTocItems(items);

  // ── Intersection observer: track active heading ──────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-110px 0px -75% 0px" },
    );

    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  // ── Scroll progress bars ─────────────────────────────────────────────────
  useEffect(() => {
    const ids = items
      .map((item) => item.id)
      .filter((id): id is string => id !== undefined);

    setScrollProgress(
      ids.reduce<Record<string, number>>((acc, id) => {
        acc[id] = 0;
        return acc;
      }, {}),
    );

    const handleScroll = () => {
      setScrollProgress((prev) => {
        const next = { ...prev };

        ids.forEach((id, index) => {
          const element = document.getElementById(id);
          if (!element) return;

          const rect = element.getBoundingClientRect();
          const nextId = ids[index + 1];
          const nextElement = nextId ? document.getElementById(nextId) : null;

          let sectionHeight: number;
          if (nextElement) {
            sectionHeight = nextElement.getBoundingClientRect().top - rect.top;
          } else {
            const proseElement = document.querySelector(".prose");
            const proseBottom = proseElement
              ? proseElement.getBoundingClientRect().bottom
              : window.innerHeight;
            sectionHeight = proseBottom - rect.top;
          }

          const viewRef = window.innerHeight / 4;
          let progress = 0;
          if (rect.top < viewRef) {
            progress = (Math.abs(rect.top - viewRef) / sectionHeight) * 100;
          }

          next[id] = Math.min(100, Math.max(0, progress));
        });

        return next;
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [items]);

  // ── Toggle a single h2 group ─────────────────────────────────────────────
  const toggleGroup = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // ── Smooth scroll helper ─────────────────────────────────────────────────
  const scrollToId = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 110;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    }
  }, []);

  if (items.length === 0) return null;

  return (
    <nav className="toc sticky top-28 self-start max-h-[calc(100vh-112px)] overflow-y-auto scroll-smooth">
      <h3 className="font-p-sm font-semibold uppercase tracking-widest text-black">
        Table of contents
      </h3>

      <div className="flex flex-col gap-3 py-5">
        <ul className="space-y-3">
          {groups.map((group) => {
            const { h2, children } = group;
            const isExpanded = expandedIds.has(h2.id);
            const hasChildren = children.length > 0;

            return (
              <li key={h2.id}>
                {/* ── Progress bar for h2 ── */}
                <div
                  className="h-1 w-full"
                  role="progressbar"
                  aria-label="Section progress"
                  aria-valuenow={Math.round((scrollProgress[h2.id] || 0) * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="h-full w-full bg-gray-300/70">
                    <div
                      className={`h-full ${activeId === h2.id ? "bg-rainbow-r2" : "bg-gray-900/30"}`}
                      style={{ width: `${scrollProgress[h2.id] || 0}%` }}
                    />
                  </div>
                </div>

                {/* ── h2 row: link + optional chevron ── */}
                <div className="mt-3 flex items-center justify-between gap-1">
                  <Link
                    href={`#${h2.id}`}
                    className={`flex-1 font-p transition-all ease-in-out ${
                      activeId === h2.id
                        ? "font-medium text-black"
                        : "text-gray-600 hover:text-black"
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(h2.id);
                    }}
                  >
                    {h2.text}
                  </Link>

                  {hasChildren && (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse section" : "Expand section"}
                      onClick={() => toggleGroup(h2.id)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-black transition-colors"
                    >
                      {/* Chevron — rotates 180° when expanded */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* ── h3 children — smooth expand/collapse via grid-rows ── */}
                {hasChildren && (
                  <ul
                    className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${
                      isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <li className="overflow-hidden">
                      <ul className="space-y-3 pt-3 pl-2">
                        {children.map((child) => (
                          <li key={child.id} className="mx-1 font-btn leading-relaxed font-light">
                            {/* Progress bar for h3 */}
                            <div
                              className="h-1 w-full"
                              role="progressbar"
                              aria-label="Subsection progress"
                              aria-valuenow={Math.round((scrollProgress[child.id] || 0) * 100)}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <div className="h-full w-full bg-gray-300/70">
                                <div
                                  className={`h-full ${activeId === child.id ? "bg-rainbow-r2" : "bg-gray-900/30"}`}
                                  style={{ width: `${scrollProgress[child.id] || 0}%` }}
                                />
                              </div>
                            </div>
                            <Link
                              href={`#${child.id}`}
                              className={`block mt-3 transition-all ease-in-out ${
                                activeId === child.id
                                  ? "font-medium text-black"
                                  : "text-gray-600 hover:text-black"
                                }`}
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToId(child.id);
                              }}
                            >
                              {child.text}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
